import { useCallback, useEffect, useMemo, useState } from 'react';
import { Card } from '@mui/material';
import { useAppSelector, useAppDispatch } from '../../../@types/redux';
import { dataSetProjects, dataSetCommodities } from '../../../store/masterData/Actions';
import { formatDateTime, isNullOrWhiteSpace, upsertArrayElement } from '../../../services/appFunctionsService';
import { generalShowErrorSnackbar, generalShowSuccessSnackbar } from '../../../store/general/Functions';
import PackmanDialog from '../../../components/dialog/PackmanDialog';
import { useHistory } from 'react-router';
import { ConfirmationDialog, CustomTable } from '@zz2/zz2-ui';
import { v4 as uuidv4 } from 'uuid';
import { IProject } from '../../../@types/model/masterData/project/project';
import ProjectHttpService from '../../../services/http/masterData/projectHttpService';
import { IProjectFormValues, ProjectFormValues } from '../../../@types/model/masterData/project/projectFormValues';
import ProjectForm from './form/ProjectForm';
import Screen from '../../../components/Screen';
import CommodityHttpService from '../../../services/http/masterData/commodityHttpService';
import { setProjectMasterDataIndexedDB, syncMasterData } from '../../../services/masterDataSyncService';
import { IApiError } from '../../../@types/other';

const ProjectScreen = () : React.ReactElement => {
    const dispatch = useAppDispatch();
    const projects = useAppSelector(x => x.masterData.projects);
    const commodities = useAppSelector(x => x.masterData.commodities);
    const auth = useAppSelector(x => x.auth);
    const pathname = useHistory()?.location?.pathname;

    const [isLoading, setIsLoading] = useState(false);
    const [selectedProject, setSelectedProject] = useState<IProject>();
    const [formGuid, setFormGuid] = useState<string>();
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isDeletePopupOpen, setIsDeletePopupOpen] = useState(false);
    const [deletingProject, setDeletingProject] = useState<IProject>();

    useEffect(() => {
        const fetchData = async () => {
            const isIndexedDBAvailable = !!self.indexedDB;
            if (isIndexedDBAvailable) {
                await syncMasterData(false);
            } else {
                setIsLoading(true);
                try {
                    const [projectRes, commodityRes] = await Promise.all([
                        ProjectHttpService.getProjectData(),
                        CommodityHttpService.getCommodityData(),
                    ]);
                    dispatch(dataSetProjects(projectRes.data));
                    dispatch(dataSetCommodities(commodityRes.data));
                } catch (error : unknown) {
                    const apiError = error as IApiError;
                    generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred while loading projects.');
                } finally {
                    setIsLoading(false);
                }
            }
        };
        fetchData();
    }, [dispatch]);

    const onSubmit = async (values : IProjectFormValues) => {
        setIsLoading(true);
        let payload = { ...values };
        if (isNullOrWhiteSpace(payload.guid)) {
            payload.guid = formGuid ?? '';
        }

        try {
            const projectModel : IProject = {
                ...payload,
                commodityId: payload.commodity?.value ? Number(payload.commodity.value) : 0,
            };

            const res = await ProjectHttpService.addOrUpdateProject(projectModel);
            const updatedList = upsertArrayElement(projects, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetProjects(updatedList));
            await setProjectMasterDataIndexedDB(updatedList);

            generalShowSuccessSnackbar(selectedProject ? 'Project updated successfully.' : 'Project added successfully.');
            closeDialog();
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred updating project data.');
        } finally {
            setIsLoading(false);
        }
    };

    const openDeleteConfirmationPopup = useCallback((project : IProject) => {
        setDeletingProject(project);
        setIsDeletePopupOpen(true);
    }, []);

    const closeDeleteConfirmationPopup = useCallback(() => {
        setDeletingProject(undefined);
        setIsDeletePopupOpen(false);
    }, []);

    const removeProject = async () => {
        if (!deletingProject) return;
        const updatedProject = { ...deletingProject, isActive: false };
        setIsLoading(true);

        try {
            const res = await ProjectHttpService.deleteProject(updatedProject.id);
            const updatedList = upsertArrayElement(projects, res.data, x => x.id === res.data.id) ?? [];
            dispatch(dataSetProjects(updatedList));
            await setProjectMasterDataIndexedDB(updatedList);
            generalShowSuccessSnackbar('Project updated successfully.');
        } catch (error : unknown) {
            const apiError = error as IApiError;
            generalShowErrorSnackbar(apiError?.data?.Message ?? 'An error occurred deleting project.');
        } finally {
            closeDeleteConfirmationPopup();
            setIsLoading(false);
        }
    };

    const closeDialog = useCallback(() => {
        setIsDialogOpen(false);
        setFormGuid(undefined);
        setSelectedProject(undefined);
    }, []);

    const openDialog = useCallback(() => {
        setFormGuid(uuidv4());
        setIsDialogOpen(true);
    }, []);

    const editProject = useCallback((project : IProject) => {
        setSelectedProject(project);
        setIsDialogOpen(true);
    }, []);

    const getCommodityCode = useCallback((id : number) => {
        const commodity = commodities?.find(x => x.id === id);
        return commodity ? commodity.code : '';
    }, [commodities]);

    const hasEditingRight = useMemo(() => {
        const rights = auth?.session?.user?.rights || [];
        return rights.some(x => pathname?.includes(x.url) && x.isActive && x.code.endsWith('_EDIT'));
    }, [auth, pathname]);

    const initialValues = useMemo(() => {
        if (isDialogOpen) {
            return ProjectFormValues(selectedProject, commodities);
        }
    }, [selectedProject, commodities, isDialogOpen]);

    return (
        <Screen isScrollable={false} isLoading={isLoading}>
            {isDialogOpen && initialValues &&
                <PackmanDialog
                    title='Project'
                    isEdit={!!selectedProject}
                    isLoading={isLoading}
                    isOpen={isDialogOpen}
                    onClose={closeDialog}
                >
                    <ProjectForm
                        isLoading={isLoading}
                        initialValues={initialValues}
                        onSubmit={onSubmit}
                        onClose={closeDialog}
                    />
                </PackmanDialog>
            }
            <Card className='fdc hfill'>
                <CustomTable<IProject>
                    enableAdding={hasEditingRight}
                    addFunction={openDialog}
                    editFunction={editProject}
                    enableEditing={hasEditingRight}
                    enableDeleting={project => project.isActive && hasEditingRight && !project.isImported}
                    deleteFunction={openDeleteConfirmationPopup}
                    enableSorting
                    enableFiltering
                    enablePagination
                    warning={row => !!row.endedOn}
                    columns={[
                        { title: 'Number', field: 'number', enableFiltering: true, enableSorting: true },
                        { title: 'Description', field: 'description', enableFiltering: true, enableSorting: true },
                        { title: 'Commodity', field: 'commodityId', formatFunction: getCommodityCode, enableFiltering: true, enableSorting: true },
                        { title: 'Ended On', field: 'endedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'Created By', field: 'createdByName', enableFiltering: true, enableSorting: true },
                        { title: 'Created On', field: 'createdOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'Updated By', field: 'updatedByName', enableFiltering: true, enableSorting: true },
                        { title: 'Updated On', field: 'updatedOn', type: 'dateTime', formatFunction: formatDateTime, enableFiltering: true, enableSorting: true },
                        { title: 'Imported?', field: 'isImported', type: 'boolean', enableFiltering: true, enableSorting: true },
                        { title: 'Active?', field: 'isActive', type: 'boolean', enableFiltering: true, enableSorting: true },
                    ]}
                    rows={projects}
                    fitWidthToPage
                    initialSortOrder={[{ columnName: 'id_Id', direction: 'asc' }]}
                    pageSizes={[50, 150, 250, 500, 1000]}
                    pageHeight={340}
                    isActive={row => row.isActive}
                />
            </Card>
            <ConfirmationDialog
                title='Delete Project'
                open={isDeletePopupOpen}
                description='Are you sure you want to delete this project?'
                onAccept={removeProject}
                onClose={closeDeleteConfirmationPopup}
                dialogType='orange'
                isLoading={isLoading}
            />
        </Screen>
    );
};

export default ProjectScreen;
